import moment from "moment";
import { useRef } from "react";
import { IVehicleLogViolationDetails } from "../screens/VehicleLogs";
import { BBOX_STROKE_COLOR, BBOX_STROKE_WIDTH } from "../utils/constants";
import { convertToTitleCase } from "../utils/commonUtils";

export interface IDimensions {
	height: number;
	width: number;
}

export interface ICoordinate {
	x: number;
	y: number;
}

export interface ICarouselOverlayDetails {
	productName: string;
	timestamp: string;
	numberPlate: string;
	speed: string;
	location: string;
	officerName: string;
	coordinates: string;
	violations: IVehicleLogViolationDetails[];
}

interface ICreateImageURLParams {
	src: string;
	overlayDetails: ICarouselOverlayDetails;
	bbox: [number, number, number, number][];
	imageDimensions: IDimensions;
	isFrame: boolean;
	violations: IVehicleLogViolationDetails[];
}

function getScaledCoordinate(
	coordinate: ICoordinate,
	imageDimensions: IDimensions,
	canvasDimensions: IDimensions,
	getCanvasCoordinate = false
): ICoordinate {
	const { height: imageHeight, width: imageWidth } = imageDimensions;
	const { height: canvasHeight, width: canvasWidth } = canvasDimensions;

	if (getCanvasCoordinate) {
		return {
			x: (canvasWidth * coordinate.x) / imageWidth,
			y: (canvasHeight * coordinate.y) / imageHeight
		};
	}

	return {
		x: (imageWidth * coordinate.x) / canvasWidth,
		y: (imageHeight * coordinate.y) / canvasHeight
	};
}

function createTopOverlayElement(canvasWidth: number, overlayDetails: ICarouselOverlayDetails): HTMLElement {
	const overlay = document.createElement("div");
	overlay.className = "evidence-image-overlay top";
	overlay.style.width = `${canvasWidth}px`;
	overlay.style.position = "absolute";
	overlay.style.top = "-99999px";
	overlay.style.left = "-99999px";
	overlay.style.visibility = "hidden";

	const headerWrapper = document.createElement("div");
	headerWrapper.className = "evidence-image-overlay-header-wrapper";

	const header = document.createElement("div");
	header.className = "evidence-image-overlay-header";

	const productName = document.createElement("h2");
	productName.textContent = overlayDetails.productName;

	const divider1 = document.createElement("div");
	divider1.className = "evidence-image-overlay-header-divider";

	const locationName = document.createElement("h3");
	locationName.textContent = overlayDetails.location;

	header.append(productName, divider1, locationName);

	const timestampWrapper = document.createElement("div");
	timestampWrapper.className = "evidence-image-overlay-timestamp-wrapper";

	const date = document.createElement("h4");
	date.textContent = `Date: ${moment(overlayDetails.timestamp).format("YYYY-MM-DD")}`;

	const divider2 = document.createElement("div");
	divider2.className = "evidence-image-overlay-header-divider";

	const time = document.createElement("h4");
	time.textContent = `Time: ${moment(overlayDetails.timestamp).format("HH:mm:ss.SSS")}`;

	timestampWrapper.append(date, divider2, time);

	headerWrapper.append(header, timestampWrapper);

	overlay.appendChild(headerWrapper);

	return overlay;
}

function createBottomOverlayElement(canvasWidth: number, overlayDetails: ICarouselOverlayDetails): HTMLElement {
	const overlay = document.createElement("div");
	overlay.className = "evidence-image-overlay bottom";
	overlay.style.width = `${canvasWidth}px`;
	overlay.style.position = "absolute";
	overlay.style.top = "-99999px";
	overlay.style.left = "-99999px";
	overlay.style.visibility = "hidden";

	const content = document.createElement("div");
	content.className = "evidence-image-overlay-content";

	const items = [
		{ label: "Plate Number", value: overlayDetails.numberPlate },
		{ label: "Officer", value: overlayDetails.officerName },
		{ label: "Location", value: overlayDetails.coordinates }
	];

	items.forEach((item) => {
		const itemDiv = document.createElement("div");
		itemDiv.className = "evidence-image-overlay-content-item";

		const h5 = document.createElement("h5");
		h5.textContent = `${item.label}: ${item.value}`;

		itemDiv.appendChild(h5);
		content.appendChild(itemDiv);
	});

	overlay.appendChild(content);

	return overlay;
}

function drawOverlayToCanvas(
	ctx: CanvasRenderingContext2D,
	overlayElement: HTMLElement,
	canvasWidth: number,
	startY: number,
	scaleFactor: number = 1,
	padding: number = 0
): number {
	const overlayPadding = padding * 0.25;

	overlayElement.style.width = `${canvasWidth}px`;
	overlayElement.style.display = "block";
	overlayElement.style.position = "absolute";
	overlayElement.style.top = "0";
	overlayElement.style.left = "0";
	overlayElement.style.visibility = "visible";
	overlayElement.style.opacity = "1";

	document.body.appendChild(overlayElement);

	void overlayElement.offsetHeight;
	void overlayElement.offsetWidth;

	const allElements = overlayElement.querySelectorAll("*");
	allElements.forEach((element) => {
		void (element as HTMLElement).offsetHeight;
		void (element as HTMLElement).offsetWidth;
	});

	if (scaleFactor !== 1) {
		const textElements = overlayElement.querySelectorAll("h2, h3, h4, h5, span");
		textElements.forEach((element) => {
			const htmlEl = element as HTMLElement;
			const computedStyle = window.getComputedStyle(htmlEl);
			const baseFontSize = parseFloat(computedStyle.fontSize) || 16;
			htmlEl.style.fontSize = `${baseFontSize * scaleFactor}px`;
		});

		void overlayElement.offsetHeight;
		allElements.forEach((element) => {
			void (element as HTMLElement).offsetHeight;
			void (element as HTMLElement).offsetWidth;
		});
	}

	const overlayHeight = overlayElement.offsetHeight;
	const overlayRect = overlayElement.getBoundingClientRect();

	const computedStyle = window.getComputedStyle(overlayElement);
	const backgroundColor = computedStyle.backgroundColor || "#000000";

	const totalHeight = overlayHeight + overlayPadding * 2;
	ctx.fillStyle = backgroundColor;
	ctx.fillRect(0, startY, canvasWidth, totalHeight);

	const drawAllElements = (element: Element) => {
		const rect = element.getBoundingClientRect();

		const relativeX = rect.left - overlayRect.left;
		const relativeY = rect.top - overlayRect.top;
		const style = window.getComputedStyle(element);

		if (element.classList.contains("evidence-image-overlay-header-divider")) {
			const scaledHeight = rect.height * scaleFactor;
			const scaledWidth = rect.width * scaleFactor;

			ctx.fillStyle = "#ffffff";
			ctx.fillRect(relativeX, startY + relativeY, scaledWidth, scaledHeight);

			return;
		}

		if (element.classList.contains("evidence-image-overlay-content-item")) {
			ctx.strokeStyle = "#ffffff";
			ctx.lineWidth = 1;
			ctx.strokeRect(relativeX, startY + overlayPadding + relativeY, rect.width, rect.height);
		}

		const isTextElement = ["H2", "H3", "H4", "H5"].includes(element.tagName);
		if (isTextElement) {
			const hasNestedTextElements = Array.from(element.children).some((child) =>
				["H2", "H3", "H4", "H5"].includes(child.tagName)
			);

			if (!hasNestedTextElements) {
				ctx.textBaseline = "middle";
				ctx.textAlign = "left";
				const fontSize = parseFloat(style.fontSize) || 16;

				const verticalCenter = startY + overlayPadding + relativeY + rect.height / 2 + 10;

				const spanElement = element.querySelector("span.evidence-image-overlay-content-name");
				if (spanElement) {
					const spanStyle = window.getComputedStyle(spanElement);
					const spanRect = spanElement.getBoundingClientRect();
					const spanX = spanRect.left - overlayRect.left;
					const labelText = spanElement.textContent || "";

					ctx.fillStyle = spanStyle.color || "#ffffff";
					const spanFontSize = parseFloat(spanStyle.fontSize) || fontSize;
					ctx.font = `${spanStyle.fontWeight} ${spanFontSize}px ${spanStyle.fontFamily}`;
					ctx.fillText(labelText, spanX, verticalCenter);

					const labelWidth = ctx.measureText(labelText).width;
					const valueText = element.textContent?.replace(labelText, "").trim() || "";
					if (valueText) {
						ctx.fillStyle = style.color || "#ffffff";
						ctx.font = `${style.fontWeight} ${fontSize}px ${style.fontFamily}`;
						ctx.fillText(valueText, spanX + labelWidth, verticalCenter);
					}
				} else {
					const text = element.textContent || "";
					if (text.trim()) {
						ctx.fillStyle = style.color || "#ffffff";
						ctx.font = `${style.fontWeight} ${fontSize}px ${style.fontFamily}`;
						ctx.fillText(text, relativeX, verticalCenter);
					}
				}
				return;
			}
		}

		Array.from(element.children).forEach((child) => drawAllElements(child));
	};

	Array.from(overlayElement.children).forEach((child) => drawAllElements(child));

	document.body.removeChild(overlayElement);

	return totalHeight;
}

function drawViolationsOverlay(
	ctx: CanvasRenderingContext2D,
	imageWidth: number,
	imageY: number,
	fontSize: number,
	padding: number,
	violations: IVehicleLogViolationDetails[],
	speed: string
) {
	if (violations.length === 0) return;

	const overlayPadding = padding * 0.5;
	const headerFontSize = fontSize * 1.25;
	const itemFontSize = fontSize;
	const itemGap = fontSize;

	const fontFamilies = `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`;

	ctx.font = `bold ${headerFontSize}px ${fontFamilies}`;
	const headerText = "Violations";
	const headerWidth = ctx.measureText(headerText).width;

	ctx.font = `${itemFontSize}px ${fontFamilies}`;
	let maxItemWidth = 0;
	violations.forEach((violation) => {
		const hasSpeedInName = violation.name.toLowerCase().includes("speed");
		const violationText = `${convertToTitleCase(violation.name.replace(/_/g, " "))}${hasSpeedInName && speed ? ` (${speed})` : ""}`;
		const itemWidth = ctx.measureText(violationText).width;
		maxItemWidth = Math.max(maxItemWidth, itemWidth);
	});

	const overlayWidth = Math.max(headerWidth, maxItemWidth) + overlayPadding * 2;
	const overlayHeight =
		headerFontSize + itemFontSize * violations.length + itemGap * violations.length + overlayPadding * 2;

	const overlayX = imageWidth - overlayWidth - padding;
	const overlayY = imageY + padding;

	ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
	ctx.fillRect(overlayX, overlayY, overlayWidth, overlayHeight);

	ctx.fillStyle = "#ffffff";
	ctx.textBaseline = "top";
	ctx.textAlign = "left";
	ctx.font = `bold ${headerFontSize}px ${fontFamilies}`;
	ctx.fillText(headerText, overlayX + overlayPadding, overlayY + overlayPadding);

	ctx.font = `${itemFontSize}px ${fontFamilies}`;
	let currentY = overlayY + overlayPadding + headerFontSize + itemGap;
	violations.forEach((violation) => {
		const hasSpeedInName = violation.name.toLowerCase().includes("speed");
		const violationText = `${convertToTitleCase(violation.name.replace(/_/g, " "))}${hasSpeedInName && speed ? ` (${speed})` : ""}`;
		ctx.fillText(violationText, overlayX + overlayPadding, currentY);
		currentY += itemFontSize + itemGap;
	});
}

export function useCreateImageURL() {
	const downloadCanvasRef = useRef<HTMLCanvasElement>(null);

	const createImageURL = (params: ICreateImageURLParams): Promise<string> => {
		const { src, overlayDetails, bbox, imageDimensions, isFrame, violations } = params;

		return new Promise((resolve, reject) => {
			if (!src) {
				reject("Image not found");
				return;
			}

			if (!downloadCanvasRef.current) {
				reject("Image canvas not found");
				return;
			}

			const canvasContext = downloadCanvasRef.current.getContext("2d");
			if (!canvasContext) {
				reject("Canvas context not found");
				return;
			}

			const backgroundImage = new Image();
			backgroundImage.crossOrigin = "anonymous";

			backgroundImage.onload = () => {
				if (!downloadCanvasRef.current) {
					reject("Image canvas not found");
					return;
				}

				try {
					const originalWidth = backgroundImage.width;
					const originalHeight = backgroundImage.height;

					const baseWidth = 854;
					const widthScale = originalWidth / baseWidth;
					const scaleFactor = widthScale;

					const baseFontSize = 16 * scaleFactor;
					const fontSize = Math.max(12, baseFontSize);
					const padding = fontSize * 2;

					const scaleOverlayFonts = (overlayElement: HTMLElement, scale: number) => {
						if (scale !== 1) {
							const textElements = overlayElement.querySelectorAll("h2, h3, h4, h5, span");
							textElements.forEach((element) => {
								const htmlEl = element as HTMLElement;
								const computedStyle = window.getComputedStyle(htmlEl);
								const baseFontSize = parseFloat(computedStyle.fontSize) || 16;
								htmlEl.style.fontSize = `${baseFontSize * scale}px`;
							});
						}
					};

					const topOverlayElement = createTopOverlayElement(originalWidth, overlayDetails);
					const bottomOverlayElement = createBottomOverlayElement(originalWidth, overlayDetails);

					scaleOverlayFonts(topOverlayElement, scaleFactor);
					scaleOverlayFonts(bottomOverlayElement, scaleFactor);

					topOverlayElement.style.width = `${originalWidth}px`;
					topOverlayElement.style.display = "block";
					topOverlayElement.style.position = "absolute";
					topOverlayElement.style.top = "0";
					topOverlayElement.style.left = "0";
					topOverlayElement.style.visibility = "visible";
					topOverlayElement.style.opacity = "1";

					bottomOverlayElement.style.width = `${originalWidth}px`;
					bottomOverlayElement.style.display = "block";
					bottomOverlayElement.style.position = "absolute";
					bottomOverlayElement.style.top = "0";
					bottomOverlayElement.style.left = "0";
					bottomOverlayElement.style.visibility = "visible";
					bottomOverlayElement.style.opacity = "1";

					document.body.appendChild(topOverlayElement);
					void topOverlayElement.offsetHeight;
					const allTopElements = topOverlayElement.querySelectorAll("*");
					allTopElements.forEach((el) => {
						void (el as HTMLElement).offsetHeight;
						void (el as HTMLElement).offsetWidth;
					});
					const topOverlayHeight = topOverlayElement.offsetHeight;
					document.body.removeChild(topOverlayElement);

					document.body.appendChild(bottomOverlayElement);
					void bottomOverlayElement.offsetHeight;
					const allBottomElements = bottomOverlayElement.querySelectorAll("*");
					allBottomElements.forEach((el) => {
						void (el as HTMLElement).offsetHeight;
						void (el as HTMLElement).offsetWidth;
					});
					const bottomOverlayHeight = bottomOverlayElement.offsetHeight;
					document.body.removeChild(bottomOverlayElement);

					const overlayPadding = padding * 0.25;

					const totalHeight =
						topOverlayHeight + overlayPadding * 2 + originalHeight + bottomOverlayHeight + overlayPadding * 2;

					downloadCanvasRef.current.width = originalWidth;
					downloadCanvasRef.current.height = totalHeight;

					canvasContext.clearRect(0, 0, originalWidth, totalHeight);

					let currentY = 0;
					const topOverlayElementForDrawing = createTopOverlayElement(originalWidth, overlayDetails);
					const topOverlayTotalHeight = drawOverlayToCanvas(
						canvasContext,
						topOverlayElementForDrawing,
						originalWidth,
						currentY,
						scaleFactor,
						padding
					);
					currentY += topOverlayTotalHeight;

					canvasContext.drawImage(backgroundImage, 0, currentY, originalWidth, originalHeight);

					if (isFrame && bbox.length > 0) {
						canvasContext.strokeStyle = BBOX_STROKE_COLOR;
						canvasContext.lineWidth = BBOX_STROKE_WIDTH * 2;

						const downloadCanvasDimensions: IDimensions = {
							width: originalWidth,
							height: originalHeight
						};

						for (const bboxItem of bbox) {
							const startCoordinate: ICoordinate = getScaledCoordinate(
								{ x: bboxItem[0], y: bboxItem[1] },
								imageDimensions,
								downloadCanvasDimensions,
								true
							);
							const bboxDimensions: ICoordinate = getScaledCoordinate(
								{ x: bboxItem[2], y: bboxItem[3] },
								imageDimensions,
								downloadCanvasDimensions,
								true
							);

							canvasContext.strokeRect(
								startCoordinate.x,
								currentY + startCoordinate.y,
								bboxDimensions.x,
								bboxDimensions.y
							);
						}
					}

					drawViolationsOverlay(canvasContext, originalWidth, currentY, fontSize, padding, violations, overlayDetails.speed);

					currentY += originalHeight;
					const bottomOverlayElementForDrawing = createBottomOverlayElement(originalWidth, overlayDetails);
					drawOverlayToCanvas(
						canvasContext,
						bottomOverlayElementForDrawing,
						originalWidth,
						currentY,
						scaleFactor,
						padding
					);

					resolve(downloadCanvasRef.current.toDataURL("image/jpeg"));
				} catch (error) {
					console.error("Error rendering overlays:", error);
					reject("Failed to create image");
				}

				backgroundImage.onload = () => {};
				backgroundImage.onerror = () => {};
			};

			backgroundImage.onerror = () => {
				reject("Failed to create image");
			};

			backgroundImage.src = src;
		});
	};

	return { createImageURL, downloadCanvasRef };
}
